import { useEffect, useState } from 'react'
import type { Vendeur } from '@troc/shared'
import { api, definirVendeurActif, vendeurActifCourant } from '../api'

/**
 * « Qui encaisse ? »
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Pourquoi la question se pose à la fin, et pas au début
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Demander avant de commencer interromprait pour rien : une vente sur cinq
 * n'aboutit pas, le client se ravise, l'article retourne en rayon. Au moment
 * de payer, la personne est devant l'écran, l'argent change de main, et le
 * choix devient un geste du paiement plutôt qu'une formalité de plus.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Des visages, pas une liste
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Un menu déroulant de quatre prénoms se choisit au hasard quand on est
 * pressé — on prend le premier. Quatre portraits se reconnaissent sans lire,
 * d'un doigt, et un visage qui n'est pas le sien se remarque : c'est ce qui
 * fait qu'on corrige au lieu de laisser passer.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Ce que ce contrôle n'est pas
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Aucun code n'est demandé. C'est un instrument de bonne tenue des comptes,
 * pas une preuve : rien n'empêche de désigner un collègue. Exiger un PIN à
 * chaque vente rendrait le comptoir impraticable, pour une boutique de quatre
 * personnes qui se connaissent.
 */

/** Le dernier choix survit à un rechargement, pas à une journée. */
const CLE = 'troc.vendeur-actif'
const OUBLI_MS = 4 * 60 * 60 * 1000

interface Memoire {
  id: number
  a: number
}

/** Rétablit le dernier vendeur choisi, s'il est encore de ce quart. */
export function restaurerVendeurActif(): number | null {
  try {
    const brut = localStorage.getItem(CLE)
    if (!brut) return null
    const memoire = JSON.parse(brut) as Memoire
    if (Date.now() - memoire.a > OUBLI_MS) return null
    definirVendeurActif(memoire.id)
    return memoire.id
  } catch {
    return null
  }
}

function retenir(id: number): void {
  definirVendeurActif(id)
  localStorage.setItem(CLE, JSON.stringify({ id, a: Date.now() } satisfies Memoire))
}

export function ChoixVendeur({
  titre = 'Qui s’en occupe ?',
  onChoisi,
  compact = false,
}: {
  titre?: string
  /** Appelé à chaque changement, y compris au rétablissement automatique. */
  onChoisi?: (vendeur: Vendeur | null) => void
  /** Rangée resserrée, pour se glisser dans un panneau déjà chargé. */
  compact?: boolean
}) {
  const [equipe, setEquipe] = useState<Vendeur[]>([])
  const [choisi, setChoisi] = useState<number | null>(() => vendeurActifCourant())

  useEffect(() => {
    api<{ equipe: Vendeur[] }>('/equipe')
      .then((r) => {
        setEquipe(r.equipe)
        const actif = vendeurActifCourant()
        if (actif !== null) onChoisi?.(r.equipe.find((v) => v.id === actif) ?? null)
      })
      .catch(() => undefined)
    // `onChoisi` change à chaque rendu du parent : le suivre relancerait la
    // requête sans fin. On ne charge l'équipe qu'une fois.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const choisir = (vendeur: Vendeur) => {
    retenir(vendeur.id)
    setChoisi(vendeur.id)
    onChoisi?.(vendeur)
  }

  return (
    <div className={compact ? 'choix-vendeur compact' : 'choix-vendeur'}>
      <div className="etiquette">{titre}</div>
      <div className="visages">
        {equipe.map((vendeur) => (
          <button
            key={vendeur.id}
            type="button"
            className={vendeur.role === 'invite' ? 'visage invite' : 'visage'}
            aria-pressed={choisi === vendeur.id}
            onClick={() => choisir(vendeur)}
          >
            <span className="portrait">
              {vendeur.photo ? (
                <img src={vendeur.photo} alt="" />
              ) : (
                <span className="initiales">{vendeur.initiales}</span>
              )}
            </span>
            <span className="prenom">{vendeur.nom}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
